import { useAdminListUsers, useListClinicActivity, type AuditEvent } from '@pa/api/admin'
import {
  DateRangeFilter,
  FilterMenu,
  fmtDateTimeOf,
  Pager,
  PAGE_SIZE,
  timeAgo,
  todayIn,
  type DateRangeValue,
} from '@pa/ui'
import { History, UserRound } from 'lucide-react'
import { actionLabel } from '../model/labels'
import { ACTIVITY_PRESETS, hasActivityFilters, toActivityParams, type ActivitySearch } from '../model/search'

export interface ActivityTabProps {
  clinicId: string
  /** The clinic's zone: times and date presets (Frontend §7.1). */
  timeZone: string
  search: ActivitySearch
  onSearch: (next: ActivitySearch, opts?: { replace?: boolean }) => void
}

const TYPE_OPTIONS = [
  { value: 'type:super_admin', label: 'Super admins' },
  { value: 'type:system', label: 'System' },
  { value: 'type:cli', label: 'Ops CLI' },
]

/** Who did it: the person, and "as super admin" when the row was written while impersonating (UserFlows S-04). */
function Who({ e }: { e: AuditEvent }) {
  const sub =
    e.impersonationId !== null
      ? 'as super admin'
      : e.actor.type === 'super_admin'
        ? 'Super admin'
        : e.actor.type === 'clinic_member'
          ? (e.actor.email ?? '')
          : e.actor.type === 'cli'
            ? 'Ops CLI'
            : 'System'
  return (
    <>
      <b style={{ fontWeight: 600 }}>{e.actor.name}</b>
      {sub && <span className="cell-sub">{sub}</span>}
    </>
  )
}

/**
 * S-04 Activity (UserFlows S-04; Frontend §7.2; Architecture P-09): the clinic's audit log, newest first (#53). Time,
 * who ("as super admin" for impersonation), action, what changed. Filters person and date; numbered pager; empty "No
 * activity yet". Read-only.
 */
export function ActivityTab({ clinicId, timeZone, search, onSearch }: ActivityTabProps) {
  const today = todayIn(timeZone)
  const params = toActivityParams(search, timeZone, today)
  const list = useListClinicActivity(clinicId, params, { query: { staleTime: 15_000 } })
  const users = useAdminListUsers(clinicId, { query: { staleTime: 60_000 } })
  const page = list.data
  const items = page?.items ?? []
  const set = (patch: Partial<ActivitySearch>) => {
    const merged: Record<string, unknown> = { ...search, ...patch, page: undefined }
    onSearch(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined)))
  }
  const filtered = hasActivityFilters(search)
  const range: DateRangeValue =
    search.range === 'custom'
      ? { id: 'custom', ...(search.from && { from: search.from }), ...(search.to && { to: search.to }) }
      : { id: search.range ?? 'any' }

  return (
    <div className="card card-fill">
      <div className="toolbar filters">
        <FilterMenu
          label="Person"
          icon={<UserRound aria-hidden />}
          value={search.actor ?? null}
          onChange={(v) => {
            set({ actor: v ?? undefined })
          }}
          allLabel="Everyone"
          options={[
            ...(users.data?.items ?? []).map((u) => ({ value: `user:${u.id}`, label: u.name })),
            ...TYPE_OPTIONS,
          ]}
        />
        <DateRangeFilter
          label="Date"
          value={range}
          presets={ACTIVITY_PRESETS}
          today={today}
          onChange={(r) => {
            set(
              r.id === 'any'
                ? { range: undefined, from: undefined, to: undefined }
                : { range: r.id, from: r.from, to: r.to },
            )
          }}
        />
        {filtered && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onSearch({})
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      <div className="table-wrap" aria-busy={list.isFetching}>
        {list.isError ? (
          <div className="empty" role="alert">
            <History aria-hidden />
            <div>Couldn&apos;t load the activity.</div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                void list.refetch()
              }}
            >
              Retry
            </button>
          </div>
        ) : list.isPending ? (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden />
            <div>Loading activity…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <History aria-hidden />
            <div>{filtered ? 'No activity matches these filters' : 'No activity yet'}</div>
          </div>
        ) : (
          <table className="table rtable activity-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Who</th>
                <th>Action</th>
                <th>What changed</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className={e.impersonationId !== null ? 'imp-row' : undefined}>
                  <td className="c-time" style={{ whiteSpace: 'nowrap' }}>
                    <b style={{ fontWeight: 600 }}>{fmtDateTimeOf(e.occurredAt, timeZone)}</b>
                    <span className="cell-sub">{timeAgo(e.occurredAt)}</span>
                  </td>
                  <td className="c-who" data-label="Who">
                    <Who e={e} />
                  </td>
                  <td className="c-action" data-label="Action">
                    {actionLabel(e.action)}
                  </td>
                  <td className="c-what" data-label="What changed">
                    {e.summary}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!list.isError && (
        <Pager
          page={page?.page ?? search.page ?? 1}
          pageSize={page?.pageSize ?? PAGE_SIZE}
          total={page?.total ?? 0}
          noun="events · newest first"
          onPage={(n) => {
            const next: ActivitySearch = { ...search }
            if (n > 1) next.page = n
            else delete next.page
            onSearch(next)
          }}
        />
      )}
    </div>
  )
}
